// React Table V8 (headless)
// https://tanstack.com/table/v8/docs/api/features/column-filtering
// https://tanstack.com/table/v8/docs/framework/react/examples/full-width-resizable-table

import React, { useMemo, useState } from 'react';
import {
    Column,
    ColumnFilter,
    ColumnSort,
    flexRender,
    getCoreRowModel,
    HeaderGroup,
    Table,
    useReactTable,
} from '@tanstack/react-table';
import { ColumnVisibility } from 'screens/modals/ManageTableColumnsModal';
import { FetchDataFn, PagingReactTable, Status } from 'types';
import ReactPlaceholder from 'react-placeholder';
import 'react-placeholder/lib/reactPlaceholder.css';
import ErrorMessageBox from 'components/ErrorMessageBox';
import { formatMessage } from 'i18n/CurrentLocale';
import { CustomPaging } from './PagingV8';
import { useSkipFirstEffect } from 'myParkdUtils/customHooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAlphaDown, faSortAlphaDownAlt } from '@fortawesome/free-solid-svg-icons';


export function Filter({ column }: { column: Column<any, unknown> }) {
    const columnFilterValue = column.getFilterValue();
    return (
        <DebouncedInput
            type="text"
            value={(columnFilterValue ?? '') as string}
            onChange={value => column.setFilterValue(value)}
            placeholder={formatMessage({ id: 'PLACEHOLDER_SEARCH' })}
        />
    )
}

function DebouncedInput({
    value: initialValue,
    onChange,
    debounce = 500,
    ...props
}: {
    value: string | number
    onChange: (value: string | number) => void
    debounce?: number
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'>) {
    const [value, setValue] = React.useState(initialValue)

    React.useEffect(() => {
        setValue(initialValue)
    }, [initialValue])

    React.useEffect(() => {
        const timeout = setTimeout(() => {
            onChange(value)
        }, debounce)

        return () => clearTimeout(timeout)
    }, [value])

    return (
        <input className='custom-filter-input' {...props} value={value} onChange={e => setValue(e.target.value)} />
    )
}

//=========================================================================================

const LoadingTableCells = ({ headerGroups }: { headerGroups: HeaderGroup<any>[] }) => {
    const loadingRows = [];
    const numRows = 10;

    for (let i = 0; i < numRows; i++) {
        loadingRows.push(
            <div className='rt8-tr' data-testid='tr-loading' key={`headerRow_${i}`}>
                {headerGroups[0].headers.map(header => {
                    return (
                        <div className='rt8-th'
                            key={`${i}_${header.id}`}
                            style={{ flex: `1 0 ${header.getSize()}px` }}
                        >
                            <div className='cell-content'>
                                <ReactPlaceholder type='textRow' showLoadingAnimation ready={false} children={null} style={{ borderRadius: '4px' }} />
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }

    return loadingRows;
}


const TableBody = ({ tableInstance, data, status }: { tableInstance: Table<any>, data: any, status: Status }) => {
    if (status.error) {
        return (
            <div style={{ padding: '1em 1em 0 1em' }}>
                <ErrorMessageBox errorMessage={formatMessage({ id: 'ERROR_SOMETHING_WENT_WRONG_TRY_AGAIN_LATER' })} />
            </div>
        )
    }
    if (status.loading) {
        return (<LoadingTableCells headerGroups={tableInstance.getHeaderGroups()} />);
    }
    if (status.succeeded && data?.length === 0) {
        return (
            <div style={{ padding: '1em 1em 0 1em' }}>
                <div className='blue-box'>{formatMessage({ id: 'REACT_TABLE_NO_DATA' })}</div>
            </div>
        )
    }

    return (
        tableInstance?.getRowModel()?.rows?.map(row => {
            return (
                <div className='rt8-tr' key={row.id}>
                    {row.getVisibleCells().map(cell => {
                        return (
                            <div className='rt8-td' key={cell.id} style={{
                                flex: `1 1 ${cell.column.getSize()}px`
                            }}>
                                <div className='cell-content'>
                                    {flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                    )}
                                </div>
                            </div>
                        )
                    })}
                </div>
            )
        })
    )
}

const TableV8 = (props: {
    data: any[];
    status: Status,
    columns: any[],
    defaultSorting: ColumnSort[],
    pagination: PagingReactTable,
    columnVisibility: ColumnVisibility,
    fetchData: FetchDataFn
}) => {

    const data = useMemo(() => props.data, [props.data]);
    const columns = useMemo(() => props.columns, []);

    const { pagination, columnVisibility, defaultSorting } = props;

    const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
    const [sorting, setSorting] = useState<ColumnSort[]>(defaultSorting);

    const tableInstance = useReactTable({
        data,
        columns,
        filterFns: {},
        state: {
            columnFilters,
            sorting,
            columnVisibility
        },
        manualPagination: true,
        manualFiltering: true,
        manualSorting: true,
        enableColumnResizing: true,
        columnResizeMode: 'onChange',
        getCoreRowModel: getCoreRowModel(),
        onColumnFiltersChange: setColumnFilters,
        onSortingChange: setSorting
    });

    useSkipFirstEffect(() => {
        props.fetchData({
            paging: { ...pagination, currentPage: 0 },
            filters: columnFilters,
            sorting
        });
    }, [columnFilters]);

    useSkipFirstEffect(() => {
        props.fetchData({
            paging: pagination,
            filters: columnFilters,
            sorting
        });
    }, [sorting]);


    return (
        <div className="react-table-v8 ReactTable parkd-react-table">
            <div className='rt8-thead'>
                {tableInstance.getHeaderGroups().map(headerGroup => (
                    <div className='rt8-tr' key={headerGroup.id}>
                        {headerGroup.headers.map(header => {
                            return (
                                <div className='rt8-th'
                                    key={header.id}
                                    style={{
                                        flex: `1 1 ${header.getSize()}px`
                                    }}
                                >
                                    {header.isPlaceholder
                                        ? null
                                        : (
                                            <>
                                                <div
                                                    {...{
                                                        className: header.column.getCanSort()
                                                            ? 'cursor-pointer select-none'
                                                            : '',
                                                        onClick: header.column.getToggleSortingHandler(),
                                                    }}
                                                >
                                                    {flexRender(
                                                        header.column.columnDef.header,
                                                        header.getContext()
                                                    )}
                                                    {{
                                                        asc: <FontAwesomeIcon icon={faSortAlphaDown} style={{ marginLeft: '0.5em' }} />,
                                                        desc: <FontAwesomeIcon icon={faSortAlphaDownAlt} style={{ marginLeft: '0.5em' }} />,
                                                    }[header.column.getIsSorted() as string] ?? null}
                                                </div>

                                                {header.column.getCanFilter() ? (
                                                    <div className="react-table-filter">
                                                        <Filter column={header.column} />
                                                    </div>
                                                ) : null}
                                            </>
                                        )}

                                    {header.column.getCanResize() && (
                                        <div
                                            onMouseDown={header.getResizeHandler()}
                                            onTouchStart={header.getResizeHandler()}
                                            className={`resizer ${header.column.getIsResizing() ? 'isResizing' : ''
                                                }`}
                                        ></div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                ))}
            </div>

            <div className='rt8-tbody'>
                <TableBody tableInstance={tableInstance} status={props.status} data={props.data} />
            </div>

            <CustomPaging
                pagination={pagination}
                fetchData={({ paging }) => { props.fetchData({ paging, filters: columnFilters, sorting }) }}
            />

        </div>
    );
};

export default TableV8;